<template>
	<div
		v-if="openBookModal"
		class="fixed inset-0 flex justify-center items-center px-4 font-['Inter',sans-serif]"
		style="z-index: 9999"
		@click.self="closeModal">
		<!-- Backdrop -->
		<div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

		<!-- Modal card -->
		<div
			:class="[isLight ? 'border-[#1a9b8a]/15 bg-white/60 backdrop-blur-xl' : 'border-white/[0.08] bg-[#0f1f1c]', 'relative w-full max-w-lg rounded-2xl border overflow-hidden shadow-2xl shadow-black/40']"
			style="max-height: 90vh; overflow-y: auto;">

			<!-- Imigongo top-right decoration -->
			<div class="absolute top-0 right-0 w-48 h-48 pointer-events-none overflow-hidden" aria-hidden="true">
				<svg class="absolute -top-6 -right-6 w-44 h-44 opacity-[0.06]" viewBox="0 0 160 160" fill="none">
					<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
						<rect x="40"  y="0"   width="28" height="28" transform="rotate(45 40  0)"   />
						<rect x="80"  y="0"   width="28" height="28" transform="rotate(45 80  0)"   />
						<rect x="120" y="0"   width="28" height="28" transform="rotate(45 120 0)"   />
						<rect x="20"  y="40"  width="28" height="28" transform="rotate(45 20  40)"  />
						<rect x="60"  y="40"  width="28" height="28" transform="rotate(45 60  40)"  />
						<rect x="100" y="40"  width="28" height="28" transform="rotate(45 100 40)"  />
					</g>
				</svg>
			</div>

			<!-- Header -->
			<div class="flex items-center justify-between px-6 pt-6 pb-4 border-b" :class="isLight ? 'border-[#1a9b8a]/15' : 'border-white/[0.06]'">
				<div>
					<p class="text-[10px] font-semibold tracking-widest uppercase mb-0.5" :class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
						{{ $t('booking.trekko') }}
					</p>
					<h2 class="font-['Montserrat'] font-extrabold text-xl tracking-tight" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
						{{ $t('booking.title') }}
					</h2>
				</div>
				<button
					@click="closeModal"
					class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200" :class="isLight ? 'border-[#1a9b8a]/20 text-[#0f1f1c]/55 hover:text-[#0f1f1c] hover:border-[#1a9b8a]/40' : 'border-white/10 text-white/40 hover:text-white hover:border-white/25'">
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
					</svg>
				</button>
			</div>

			<!-- ══════════════════════════════ STRIPE PAYMENT VIEW══════════════════════════════ -->
			<div v-if="showStripePayment" class="px-6 py-5 space-y-4">

				<!-- Ticket summary -->
				<div class="rounded-xl border px-4 py-3.5 space-y-1" :class="isLight ? 'bg-[#1a9b8a]/12 border-[#1a9b8a]/25' : 'bg-[#1a9b8a]/10 border-[#1a9b8a]/20'">
					<p class="text-[11px] font-semibold tracking-widest uppercase mb-2" :class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">{{ $t('booking.orderSummary') }}</p>
					<div class="flex justify-between text-sm">
						<span :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/60'">{{ $t('booking.ticket') }}</span>
						<span class="font-medium" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ ticket.name }}</span>
					</div>
					<div class="flex justify-between text-sm">
						<span :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/60'">{{ $t('booking.price') }}</span>
						<span class="font-medium" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ formatCurrency(localTicket.price) }}</span>
					</div>
					<div class="flex justify-between text-sm">
						<span :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/60'">{{ $t('booking.quantity') }}</span>
						<span class="font-medium" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ localTicket.quantity }}</span>
					</div>
					<div class="pt-2 mt-2 flex justify-between border-t" :class="isLight ? 'border-[#1a9b8a]/20' : 'border-white/[0.07]'">
						<span class="text-sm font-bold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ $t('booking.total') }}</span>
						<span class="text-sm font-bold" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">{{ formatCurrency(localTicket.price * localTicket.quantity) }}</span>
					</div>
				</div>

				<!-- Countdown -->
				<div class="flex items-center gap-2 rounded-xl border px-4 py-2.5" :class="isLight ? 'border-red-400/40 bg-red-50 text-red-600' : 'border-red-500/20 bg-red-500/10 text-red-400'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
					</svg>
					<p class="text-sm font-medium">
						{{ $t('booking.reservationExpires') }} <span class="font-bold">{{ reservation.countdown }}{{ $t('booking.seconds') }}</span>
					</p>
				</div>

				<!-- Network messages -->
				<div v-if="networkFailureMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm" :class="isLight ? 'border-amber-400/40 bg-amber-50 text-amber-700' : 'border-amber-400/20 bg-amber-400/10 text-amber-400'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
					</svg>
					{{ networkFailureMessage }}
				</div>
				<div v-if="networkOnlineMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm" :class="isLight ? 'border-[#1a9b8a]/30 bg-[#1a9b8a]/12 text-[#0d6b5e]' : 'border-[#1a9b8a]/25 bg-[#1a9b8a]/10 text-[#a8d5cc]'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
					</svg>
					{{ networkOnlineMessage }}
				</div>

				<!-- Stripe element -->
				<div>
					<p class="text-sm mb-3 text-center" :class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/50'">{{ $t('booking.completePayment') }}</p>
					<div id="stripe-payment-element" class="mb-4 rounded-xl overflow-hidden"></div>
				</div>

				<!-- Pay button -->
				<button
					@click="confirmPayment"
					:disabled="isPaymentProcessing || reservation.isExpired"
					class="w-full py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
					:class="reservation.isExpired ? 'bg-white/10 text-white/30' : 'bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20'">
					<svg v-if="isPaymentProcessing" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
					</svg>
					<span>{{ isPaymentProcessing ? $t('booking.processing') : $t('booking.pay') + ' ' + formatCurrency(localTicket.price * localTicket.quantity) }}</span>
				</button>
				<button
					@click="cancelPayment"
					class="w-full text-center text-sm transition-colors duration-200 mt-1" :class="isLight ? 'text-[#0f1f1c]/50 hover:text-[#0f1f1c]/80' : 'text-white/30 hover:text-white/60'">
					{{ $t('booking.cancel') }}
				</button>
			</div>

			<!-- ══════════════════════════════REGULAR BOOKING FORM ══════════════════════════════ -->
			<form v-else @submit.prevent="handleSubmit" class="px-6 py-5 space-y-5">

				<!-- Ticket summary -->
				<div class="rounded-xl border px-4 py-3.5 space-y-1" :class="isLight ? 'bg-[#1a9b8a]/12 border-[#1a9b8a]/25' : 'bg-[#1a9b8a]/10 border-[#1a9b8a]/20'">
					<p class="text-[11px] font-semibold tracking-widest uppercase mb-2" :class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">{{ $t('booking.orderSummary') }}</p>
					<div class="flex justify-between text-sm">
						<span :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/60'">{{ $t('booking.ticket') }}</span>
						<span class="font-medium" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ ticket.name }}</span>
					</div>
					<div class="flex justify-between text-sm">
						<span :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/60'">{{ $t('booking.price') }}</span>
						<span class="font-medium" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ formatCurrency(localTicket.price) }}</span>
					</div>
					<div class="flex justify-between text-sm">
						<span :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/60'">{{ $t('booking.quantity') }}</span>
						<span class="font-medium" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ localTicket.quantity }}</span>
					</div>
					<div class="pt-2 mt-2 flex justify-between border-t" :class="isLight ? 'border-[#1a9b8a]/20' : 'border-white/[0.07]'">
						<span class="text-sm font-bold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ $t('booking.total') }}</span>
						<span class="text-sm font-bold" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">{{ formatCurrency(localTicket.price * localTicket.quantity) }}</span>
					</div>
				</div>

				<!-- Quantity -->
				<div>
					<p class="text-[11px] font-semibold tracking-widest uppercase mb-3" :class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/40'">{{ $t('booking.quantity') }}</p>
					<div class="flex items-center gap-4">
						<button type="button" @click="decreaseQuantity"
							:disabled="localTicket.quantity <= 1 || isEmailLoading || isOtpLoading || event.private"
							class="w-9 h-9 rounded-full border flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 text-lg font-bold" :class="isLight ? 'border-[#1a9b8a]/20 text-[#0d6b5e] hover:border-[#1a9b8a]/40 hover:bg-[#1a9b8a]/8' : 'border-white/10 text-white/60 hover:border-[#1a9b8a]/40 hover:text-[#a8d5cc]'">
							−
						</button>
						<span class="text-xl font-bold w-6 text-center" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ localTicket.quantity }}</span>
						<button type="button" @click="increaseQuantity"
							:disabled="isEmailLoading || isOtpLoading || event.private"
							class="w-9 h-9 rounded-full border flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 text-lg font-bold" :class="isLight ? 'border-[#1a9b8a]/20 text-[#0d6b5e] hover:border-[#1a9b8a]/40 hover:bg-[#1a9b8a]/8' : 'border-white/10 text-white/60 hover:border-[#1a9b8a]/40 hover:text-[#a8d5cc]'">
							+
						</button>
					</div>
				</div>

				<!-- Status messages — isLight aware -->
				<div v-if="successMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm"
					:class="isLight ? 'border-[#1a9b8a]/30 bg-[#1a9b8a]/12 text-[#0d6b5e]' : 'border-[#1a9b8a]/25 bg-[#1a9b8a]/10 text-[#a8d5cc]'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
					{{ successMessage }}
				</div>
				<div v-if="errorMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm"
					:class="isLight ? 'border-red-400/40 bg-red-50 text-red-600' : 'border-red-500/25 bg-red-500/10 text-red-400'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
					{{ errorMessage }}
				</div>
				<div v-if="infoMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm"
					:class="isLight ? 'border-blue-400/40 bg-blue-50 text-blue-600' : 'border-blue-400/20 bg-blue-400/10 text-blue-400'">
					<svg class="w-4 h-4 flex-shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
					{{ infoMessage }}
				</div>
				<div v-if="warningMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm"
					:class="isLight ? 'border-amber-400/40 bg-amber-50 text-amber-700' : 'border-amber-400/20 bg-amber-400/10 text-amber-400'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
					{{ warningMessage }}
				</div>
				<div v-if="networkFailureMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm"
					:class="isLight ? 'border-amber-400/40 bg-amber-50 text-amber-700' : 'border-amber-400/20 bg-amber-400/10 text-amber-400'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
					{{ networkFailureMessage }}
				</div>
				<div v-if="networkOnlineMessage"
					class="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm"
					:class="isLight ? 'border-[#1a9b8a]/30 bg-[#1a9b8a]/12 text-[#0d6b5e]' : 'border-[#1a9b8a]/25 bg-[#1a9b8a]/10 text-[#a8d5cc]'">
					<svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
					{{ networkOnlineMessage }}
				</div>

				<!-- Email -->
				<div>
					<label class="block text-[11px] font-semibold tracking-widest uppercase mb-2" :class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/40'">{{ $t('booking.emailLabel') }}</label>
					<input
						v-model="attendee.email"
						type="email"
						required
						:disabled="isOtpLoading"
						class="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-all duration-200 disabled:opacity-40" :class="isLight ? 'bg-white/70 border-[#1a9b8a]/25 text-[#0f1f1c] placeholder-[#0f1f1c]/40 focus:border-[#1a9b8a]/50 focus:bg-white/90' : 'bg-white/[0.04] border-white/[0.08] text-white placeholder-white/20 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'"
						:placeholder="$t('booking.emailPlaceholder')"
						@blur="debouncedValidateEmail" />
					<p v-if="emailError" class="text-red-400 text-xs mt-1.5 flex items-center gap-1">
						<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
						{{ emailError }}
					</p>
					<div v-if="isEmailLoading" class="flex items-center gap-2 text-xs text-blue-400 mt-1.5">
						<svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
						{{ $t('booking.verifyingEmail') }}
					</div>
				</div>

				<!-- OTP -->
				<div v-if="!isEmailVerified && otpSent && !otpExpired">
					<label class="block text-[11px] font-semibold tracking-widest uppercase mb-2" :class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/40'">{{ $t('booking.otpLabel') }}</label>
					<p class="text-xs mb-2" :class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/40'">{{ $t('booking.otpSent') }}</p>
					<input
						v-model="otp"
						type="text"
						required
						aria-label="Enter OTP"
						:disabled="isOtpLoading"
						class="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-all duration-200 disabled:opacity-40 tracking-[0.3em] font-mono" :class="isLight ? 'bg-white/70 border-[#1a9b8a]/25 text-[#0f1f1c] placeholder-[#0f1f1c]/40 focus:border-[#1a9b8a]/50 focus:bg-white/90' : 'bg-white/[0.04] border-white/[0.08] text-white placeholder-white/20 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'"
						:placeholder="$t('booking.otpPlaceholder')"
						@blur="verifyOtp" />
					<p v-if="otpError" class="text-red-400 text-xs mt-1.5">{{ otpError }}</p>
					<div v-if="isOtpLoading" class="flex items-center gap-2 text-xs text-blue-400 mt-1.5">
						<svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
						{{ $t('booking.verifyingCode') }}
					</div>
					<p v-if="countdown && !otpExpired" class="text-xs mt-1.5" :class="isLight ? 'text-[#0f1f1c]/55' : 'text-white/30'">
						{{ $t('booking.expiresIn') }} <span class="font-medium" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">{{ countdown }}{{ $t('booking.seconds') }}</span>
					</p>
				</div>

				<!-- OTP expired -->
				<div v-if="otpExpired" class="space-y-2">
					<p class="text-xs text-red-400">{{ $t('booking.otpExpired') }}</p>
					<button v-if="!isOtpLoading" type="button" @click="resetAndSendOtp()"
						:disabled="otpRetries >= maxOtpRetries"
						class="text-xs font-semibold transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed" :class="isLight ? 'text-[#1a9b8a] hover:text-[#0d6b5e]' : 'text-[#a8d5cc] hover:text-white'">
						{{ $t('booking.resendCode') }}
					</button>
					<p v-if="otpRetries >= maxOtpRetries" class="text-red-400 text-xs">
						{{ $t('booking.maxAttempts') }}
					</p>
				</div>

				<!-- Name -->
				<div>
					<label class="block text-[11px] font-semibold tracking-widest uppercase mb-2" :class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/40'">{{ $t('booking.nameLabel') }}</label>
					<input
						v-model="attendee.name"
						type="text"
						required
						:disabled="isEmailLoading || isOtpLoading || !isEmailVerified"
						class="w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-all duration-200 disabled:opacity-30" :class="isLight ? 'bg-white/70 border-[#1a9b8a]/25 text-[#0f1f1c] placeholder-[#0f1f1c]/40 focus:border-[#1a9b8a]/50 focus:bg-white/90' : 'bg-white/[0.04] border-white/[0.08] text-white placeholder-white/20 focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'"
						:placeholder="$t('booking.namePlaceholder')" />
				</div>

				<!-- Phone + Country picker -->
				<div class="phone-input-root relative">
					<label class="block text-[11px] font-semibold tracking-widest uppercase mb-2"
						:class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/40'">{{ $t('booking.phoneLabel') }}</label>

					<!-- Row: country button + number input -->
					<div class="flex gap-2">
						<!-- Country selector -->
						<button type="button"
							@click="!(isEmailLoading || isOtpLoading || !isEmailVerified) && (showCountryPicker = !showCountryPicker)"
							:disabled="isEmailLoading || isOtpLoading || !isEmailVerified"
							class="flex items-center gap-1.5 px-3 py-3 rounded-xl border text-sm font-semibold flex-shrink-0 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
							:class="showCountryPicker
								? (isLight ? 'border-[#1a9b8a]/50 bg-[#1a9b8a]/8' : 'border-[#1a9b8a]/50 bg-[#1a9b8a]/10')
								: (isLight ? 'border-[#1a9b8a]/25 bg-white/70 hover:border-[#1a9b8a]/40' : 'border-white/[0.08] bg-white/[0.04] hover:border-[#1a9b8a]/30')">
							<span class="text-base leading-none">{{ phoneSelected.flag }}</span>
							<span :class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/70'">{{ phoneSelected.dialCode }}</span>
							<svg class="w-3 h-3 transition-transform duration-200"
								:class="[showCountryPicker ? 'rotate-180' : '', isLight ? 'text-[#0f1f1c]/40' : 'text-white/30']"
								fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
							</svg>
						</button>

						<!-- Number input — digits only, live validation -->
						<input
							:value="phoneLocal"
							type="tel"
							inputmode="numeric"
							:disabled="isEmailLoading || isOtpLoading || !isEmailVerified"
							class="flex-1 px-4 py-3 rounded-xl border text-sm focus:outline-none transition-all duration-200 disabled:opacity-30"
							:class="[
								phoneError
									? (isLight ? 'border-red-400/60 bg-red-50/60' : 'border-red-500/40 bg-red-500/5')
									: (isLight ? 'bg-white/70 border-[#1a9b8a]/25 focus:border-[#1a9b8a]/50 focus:bg-white/90' : 'bg-white/[0.04] border-white/[0.08] focus:border-[#1a9b8a]/50 focus:bg-[#1a9b8a]/5'),
								isLight ? 'text-[#0f1f1c] placeholder-[#0f1f1c]/40' : 'text-white placeholder-white/20',
							]"
							:placeholder="$t('booking.phonePlaceholder')"
							@keydown="onPhoneKeydown"
							@input="onPhoneInput" />
					</div>

					<!-- Country dropdown -->
					<div v-if="showCountryPicker"
						class="absolute z-50 mt-1 w-full rounded-xl border shadow-xl overflow-hidden"
						:class="isLight ? 'bg-white border-[#1a9b8a]/20 shadow-black/10' : 'bg-[#132420] border-white/[0.08] shadow-black/40'">
						<!-- Search -->
						<div class="px-3 pt-3 pb-2">
							<div class="flex items-center gap-2 px-3 py-2 rounded-lg border"
								:class="isLight ? 'border-[#1a9b8a]/20 bg-[#1a9b8a]/5' : 'border-white/[0.08] bg-white/[0.04]'">
								<svg class="w-3.5 h-3.5 flex-shrink-0"
									:class="isLight ? 'text-[#1a9b8a]/60' : 'text-[#a8d5cc]/50'"
									fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
								</svg>
								<input v-model="countrySearch" type="text" :placeholder="$t('booking.searchCountry')"
									class="flex-1 bg-transparent text-xs focus:outline-none"
									:class="isLight ? 'text-[#0f1f1c] placeholder-[#0f1f1c]/35' : 'text-white placeholder-white/25'" />
							</div>
						</div>
						<!-- List -->
						<div class="max-h-48 overflow-y-auto">
							<button v-for="country in filteredCountries"
								:key="country.code + country.dialCode"
								type="button"
								@click="onPickCountry(country)"
								class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors duration-150"
								:class="phoneSelected.code === country.code && phoneSelected.dialCode === country.dialCode
									? (isLight ? 'bg-[#1a9b8a]/12 text-[#0d6b5e] font-semibold' : 'bg-[#1a9b8a]/15 text-[#a8d5cc] font-semibold')
									: (isLight ? 'text-[#0f1f1c]/70 hover:bg-[#1a9b8a]/8' : 'text-white/60 hover:bg-white/[0.05]')">
								<span class="text-base leading-none w-6 text-center flex-shrink-0">{{ country.flag }}</span>
								<span class="flex-1 truncate">{{ country.name }}</span>
								<span class="text-xs flex-shrink-0" :class="isLight ? 'text-[#1a9b8a]/70' : 'text-[#a8d5cc]/60'">{{ country.dialCode }}</span>
							</button>
							<p v-if="filteredCountries.length === 0"
								class="px-4 py-3 text-xs text-center"
								:class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/30'">{{ $t('booking.noCountries') }}</p>
						</div>
					</div>

					<!-- Error -->
					<p v-if="phoneError" class="text-xs mt-1.5 flex items-center gap-1"
						:class="isLight ? 'text-red-600' : 'text-red-400'">
						<svg class="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
						</svg>
						{{ phoneError }}
					</p>
				</div>

				<!-- Payment notice (offline/unavailable) -->
				<div v-if="ticket.price > 0 && !acceptPayment"
					class="flex items-start gap-2 rounded-xl border px-4 py-3 text-sm" :class="isLight ? 'border-amber-400/40 bg-amber-50 text-amber-700' : 'border-amber-400/20 bg-amber-400/10 text-amber-400'">
					<svg class="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
					</svg>
					{{ $t('booking.payOffline') }}
				</div>

				<!-- Terms -->
				<div class="flex items-start gap-3">
					<div class="relative flex-shrink-0 mt-0.5">
						<input
							:checked="isTermsChecked"
							@change="updateTerms($event.target.checked)"
							type="checkbox"
							required
							:disabled="isEmailLoading || isOtpLoading || !isEmailVerified"
							class="sr-only peer" />
						<div
							@click="!isEmailLoading && !isOtpLoading && isEmailVerified && updateTerms(!isTermsChecked)"
							class="w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all duration-200"
							:class="isTermsChecked
								? 'bg-[#1a9b8a] border-[#1a9b8a]'
								: isLight ? 'bg-transparent border-black/20 hover:border-[#1a9b8a]/40' : 'bg-transparent border-white/20 hover:border-[#1a9b8a]/40'">
							<svg v-if="isTermsChecked" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
								<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
							</svg>
						</div>
					</div>
					<label class="text-sm leading-snug" :class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/50'">
						{{ $t('booking.agreeTerms') }}
						<a href="/privacy-policy" class="transition-colors duration-200 underline underline-offset-2" :class="isLight ? 'text-[#1a9b8a] hover:text-[#0d6b5e]' : 'text-[#a8d5cc] hover:text-white'">
							{{ $t('booking.termsLink') }}
						</a>
					</label>
				</div>

				<!-- Submit -->
				<div class="pt-1">
					<button
						:disabled="
							!attendee.name ||
							!attendee.email ||
							!attendee.phone ||
							!!phoneError ||
							!ticket.name ||
							!localTicket.quantity ||
							!isEmailVerified ||
							!otpValid ||
							isEmailLoading ||
							isOtpLoading ||
							submitting
						"
						type="submit"
						class="w-full py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20">
						<svg v-if="submitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
						</svg>
						<span>{{ submitting ? $t('booking.processing') : $t('booking.bookNow') }}</span>
					</button>
				</div>
			</form>
		</div>
	</div>

	<RecommendMusicModal
		v-if="isRecommendMusicModalOpen"
		:isOpen="isRecommendMusicModalOpen"
		@close="isRecommendMusicModalOpen = false"
		:event="event"
		:hideEmail="true"
		:email="attendee.email" />
</template>

<script>
import apiService from "@/services/api";
import { debounce } from "lodash";
import { useToast } from "vue-toastification";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { loadStripe } from "@stripe/stripe-js";
import { usePhoneInput } from "@/composables/usePhoneInput";
import RecommendMusicModal from "./RecommendMusicModal.vue";

library.add(faSpinner);

export default {
	components: {
		RecommendMusicModal,
	},
	props: {
		isOpen: {
			type: Boolean,
			required: true,
		},
		ticket: Object,
		event: Object,
		isLight: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["close"],
	setup() {
		const toast = useToast();
		const { selected, localNumber, showCountryPicker, search, filtered, pick,	phoneError,
 } = usePhoneInput();
		return {
			toast,
			phoneSelected: selected,
			phoneLocal: localNumber,
			showCountryPicker,
			countrySearch: search,
			filteredCountries: filtered,
			pickCountry: pick,
			phoneError,
		};
	},
	data() {
		return {
			reservation: {
				paymentIntentId: null,
				expiresAt: null,
				countdown: 180,
				timer: null,
				poller: null,
				isExpired: false,
				failedChecks: 0,
				isOffline: !navigator.onLine,
			},
			openBookModal: false,
			successMessage: "",
			errorMessage: "",
			infoMessage: "",
			warningMessage: "",
			networkFailureMessage: "",
			networkOnlineMessage: "",
			messageTimeout: null,
			attendee: {
				name: "",
				email: "",
				phone: "",
			},
			payment: {
				method: "Credit Card",
			},
			acceptPayment: true,
			isTermsChecked: false,
			isEmailVerified: false,
			otp: "",
			otpSent: false,
			otpValid: false,
			otpExpiryTime: null,
			otpInterval: null,
			otpRetries: 0,
			maxOtpRetries: 3,
			countdown: 0,
			otpExpired: false,
			isEmailLoading: false,
			isOtpLoading: false,
			enableRecommendMusicSection: false,
			isRecommendMusicModalOpen: false,
			isEmailHidded: false,
			submitting: false,  
			showStripePayment: false,
			isPaymentProcessing: false,
			stripe: null,
			stripeElements: null,
			paymentIntentData: null,
			localTicket: {
				...this.ticket,
				quantity: this.ticket?.quantity || 1,
				price: this.ticket?.price || 0,
			},
		};
	},

	watch: {

		isOpen: {
			immediate: true,
			handler(newVal) {
				this.openBookModal = newVal;
				if (!newVal) {
					this.showStripePayment = false;
					this.paymentIntentData = null;
				}
			},
		},

	},
	created() {
		this.debouncedValidateEmail = debounce(this.validateEmail, 300);
	},
	mounted() {
		window.addEventListener("online", this.handleOnline);
		window.addEventListener("offline", this.handleOffline);
	},
	beforeUnmount() {
		window.removeEventListener("online", this.handleOnline);
		window.removeEventListener("offline", this.handleOffline);
		this.clearReservationTimers();
	},
	methods: {

		// ── Phone input handlers ──────────────────────────────────────────────
		onPhoneKeydown(e) {
			// Allow control keys
			const ctrl = ['Backspace','Delete','Tab','Enter','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'];
			if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return;
			// Block anything that is not a digit
			if (!/^\d$/.test(e.key)) e.preventDefault();
		},
		onPhoneInput(e) {
			// Strip any non-digit that slipped through (paste, autofill, etc.)
			const digits = e.target.value.replace(/\D/g, '');
			e.target.value = digits;            // keep DOM clean
			this.phoneLocal = digits;           // update composable ref for display
			this.attendee.phone = this.phoneSelected.dialCode + digits;
			this.runPhoneValidation(digits);
		},
		onPickCountry(country) {
			this.pickCountry(country);          // composable: closes picker, resets search
			const digits = this.phoneLocal.replace(/\D/g, '');
			this.attendee.phone = country.dialCode + digits;
			if (digits) this.runPhoneValidation(digits, country);
		},
		runPhoneValidation(digits, country) {
			const c = country || this.phoneSelected;
			if (!digits) { this.phoneError = ''; return; }
			if (digits.length < c.min || digits.length > c.max) {
				const range = c.min === c.max ? `${c.min}` : `${c.min}–${c.max}`;
				this.phoneError = `${c.name} numbers require ${range} digits`;
			} else {
				this.phoneError = '';
			}
		},
		// ───────────────────────────────────────────────────────────────────────
		closeModal() {
			this.clearReservationTimers();
			this.showStripePayment = false;
			this.paymentIntentData = null;
			this.openBookModal = false;
			this.$emit("close");
		},
		formatCurrency(value) {
			if (isNaN(value)) return "$0.00";
			return `$${value.toFixed(2)}`;
		},
		increaseQuantity() {
			this.localTicket.quantity++;
		},
		decreaseQuantity() {
			if (this.localTicket.quantity > 1) {
				this.localTicket.quantity--;
			}
		},
		validateEmail() {
			if (this.isEmailLoading || this.isOtpLoading) return;
			const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
			if (!emailRegex.test(this.attendee.email)) {
				this.isEmailVerified = false;
				this.setMessage("error", "Please enter a valid email address");
				return;
			}
			this.emailError = "";
			this.checkEmailExists();
		},
		openRecommendMusicModal() {
			this.isRecommendMusicModalOpen = true;
		},
		async checkEmailExists() {
			this.isEmailLoading = true;
			try {
				const response = await apiService.emailVerification(this.attendee.email);
				if (response?.data?.exists) {
					if (this.otpInterval) {
						clearInterval(this.otpInterval);
						this.otpInterval = null;
					}

					this.otpValid = true;
					this.isEmailVerified = true;
					this.otpSent = false;
					this.otpExpired = false;
					this.countdown = 0;

					this.setMessage("success", "Email Verified");
				} else {
					if (!this.otpSent) {
						await this.sendOtp(this.attendee.email);
					}
				}
			} catch (error) {
				this.setMessage("error", "Email verification failed");
			} finally {
				this.isEmailLoading = false;
			}
		},
		async sendOtp(destination) {
			if (this.otpExpired || this.isOtpLoading) return;
			this.isOtpLoading = true;
			this.otpError = "";
			this.setMessage("info", "Sending OTP to your email..");
			if (this.otpRetries >= this.maxOtpRetries) {
				this.isOtpLoading = false;
				this.setMessage("error", "Too many attempts. Please try again later or contact support.");
				return;
			}
			try {
				const response = await apiService.sendOtp(destination);
				if (response.data.sent) {
					this.otpSent = true;
					this.isEmailVerified = false;
					this.otpExpired = false;
					this.otpRetries++;
					this.otpExpiryTime = Date.now() + 2 * 60 * 1000;
					this.startCountdown();
					this.setMessage("info", "OTP sent successfully! Check your email.");
				} else {
					this.otpError = "Failed to send OTP. Please try again.";
					this.toast.error("Failed to send OTP. Please try again.");
				}
			} catch (error) {
				this.setMessage("networkFailure", "Network error. Please check your connection.");
			} finally {
				this.isOtpLoading = false;
			}
		},
		startCountdown() {
			if (this.otpInterval) {
				clearInterval(this.otpInterval);
			}
			this.countdown = 120;
			const interval = setInterval(() => {
				if (this.isEmailVerified) {
					clearInterval(this.otpInterval);
					this.otpInterval = null;
					return;
				}

				if (this.countdown > 0) {
					this.countdown--;
				} else {
					this.otpExpired = true;
					clearInterval(interval);
					this.toast.warning("OTP has expired");
				}
			}, 1000);
		},
		async verifyOtp() {
			if (this.otpExpired) {
				this.setMessage("error", "OTP has expired. Please request a new one.");
				return;
			}
			if (!this.otp) {
				this.setMessage("error", "Please enter the OTP code");
				return;
			}
			this.isOtpLoading = true;
			this.setMessage("info", "Verifying OTP...");
			try {
				const response = await apiService.verifyOtp(this.attendee.email, this.otp);
				if (response.data.verified) {
					// Stop countdown
					if (this.otpInterval) {
						clearInterval(this.otpInterval);
						this.otpInterval = null;
					}
					this.isEmailVerified = true;
					this.otpValid = true;
					// Reset OTP state
					this.otpSent = false;
					this.otpExpired = false;
					this.countdown = 0;
					this.otp = "";

					this.setMessage("success", "Email verified successfully!");

				} else {
					this.setMessage("error", "Invalid OTP code. Please try again.");
				}
			} catch (error) {
				this.toast.error("Verification failed. Please try again.");
			} finally {
				this.isOtpLoading = false;
			}
		},
		resetAndSendOtp() {
			if (this.otpRetries >= this.maxOtpRetries) {
				this.setMessage("error", "Maximum OTP attempts reached. Please try again later.");
				return;
			}
			// clear previous countdown and OTP state
			if (this.otpInterval) {
				clearInterval(this.otpInterval);
				this.otpInterval = null;
			}

			this.otpExpired = false;
			this.setMessage("info", "Resending OTP...");
			this.sendOtp(this.attendee.email);
		},
		updatePayment(field, value) {
			this.payment[field] = value;
		},
		updateTerms(value) {
			this.isTermsChecked = value;
		},
		async handleSubmit() {
			if (!this.isEmailVerified || !this.otpValid) {
				this.toast.error("Please verify your email and OTP first");
				return;
			}
			if (!this.isTermsChecked) {
				this.toast.error("Please accept the terms and conditions");
				return;
			}
			if (!this.payment.method && this.ticket.price > 0) {
				this.toast.error("Please select a payment method.");
				return;
			}
			this.submitting = true;
			this.setMessage("info", "Reserving your spot");
			try {
				const ticket = {
					eventId: this.event.event_id,
					ticketType: this.ticket.name,
					price: parseFloat(this.localTicket.price),
					quantity: this.localTicket.quantity,
					purchaseDate: new Date().toISOString(),
					activated: false,
				};
				const response = await apiService.bookEvent(this.attendee, ticket, this.payment);
				if (response?.status === "Created") {
					const responseData = response.data;
					if (responseData.client_secret) {
						this.paymentIntentData = responseData;
						this.openBookModal = false;
						this.showStripePayment = true;
						this.openBookModal = true;
						const clientSecret = responseData.client_secret;
						this.reservation.paymentIntentId = clientSecret.split("_secret")[0];
						await this.initializeStripePayment(responseData.client_secret);
						this.startReservationFlow();
					} else {
						this.ticketPurchased = true;
						this.toast.success("Registration Successful! Check your email for more information");
						this.isRecommendMusicModalOpen = this.enableRecommendMusicSection ? true : false;
						this.closeModal();
					}
				}
			} catch (error) {
				if (error.response && error.response.data && error.response.data.error === "User already bought a ticket") {
					this.setMessage("warning", "You have already bought a ticket.");
				} else {
					this.toast.error("Booking failed. Please try again.");
				}
			} finally {
				this.submitting = false;
			}
		},
		async initializeStripePayment(clientSecret) {
			const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
			if (!publishableKey) {
				this.toast.error("Payment system not configured");
				return;
			}
			this.stripe = await loadStripe(publishableKey);
			const elements = this.stripe.elements({
				clientSecret: clientSecret,
				appearance: {
					theme: "night",
					variables: {
						colorPrimary: "#1a9b8a",
						colorBackground: "#132420",
						colorText: "#ffffff",
						colorDanger: "#ef4444",
						fontFamily: "Inter, sans-serif",
						borderRadius: "12px",
					},
				},
			});
			const paymentElement = elements.create("payment");
			paymentElement.mount("#stripe-payment-element");
			this.stripeElements = elements;
		},
		async confirmPayment() {
			if (!this.stripe || !this.stripeElements) {
				this.toast.error("Payment system not ready");
				return;
			}
			this.isPaymentProcessing = true;
			const { error } = await this.stripe.confirmPayment({
				elements: this.stripeElements,
				confirmParams: {
					return_url: `${window.location.origin}/payment/success`,
				},
				redirect: "if_required",
			});
			this.isPaymentProcessing = false;
			if (error) {
				this.toast.error(error.message || "Payment failed. Please try again.");
			} else {
				this.clearReservationTimers();
				this.toast.success("Payment successful! Your tickets are confirmed.");
				this.showStripePayment = false;
				this.closeModal();
				if (this.enableRecommendMusicSection) {
					this.isRecommendMusicModalOpen = true;
				}
			}
		},
		cancelPayment() {
			this.showStripePayment = false;
			this.paymentIntentData = null;
			this.closeModal();
			this.toast.info("Payment cancelled. Your booking was not completed.");
		},
		startReservationFlow() {
			this.reservation.countdown = 180;
			this.reservation.isExpired = false;
			this.reservation.failedChecks = 0;
			this.reservation.timer = setInterval(() => {
				if (this.reservation.countdown > 0) {
					this.reservation.countdown--;
				} else {
					this.handleExpiry();
				}
			}, 1000);
			this.reservation.poller = setInterval(() => {
				this.checkTicketStatus();
			}, 20000);
		},
		async checkTicketStatus() {
			if (!this.reservation.paymentIntentId) return;
			try {
				const res = await apiService.checkTicketStatus(this.reservation.paymentIntentId);
				this.networkFailureMessage = "";
				this.reservation.failedChecks = 0;
				if (!res.data.ticket_status) {
					this.handleExpiry(true);
				}
			} catch (err) {
				this.reservation.failedChecks++;
				if (this.reservation.failedChecks <= 2) {
					this.setMessage("networkFailure", "Connection issue. Retrying...");
				} else {
					this.toast.error("Having trouble verifying your reservation...");
				}
			}
		},
		async handleExpiry(confirmedByBackend = false) {
			if (this.reservation.isExpired) return;
			if (!confirmedByBackend) {
				if (this.reservation.isOffline || this.reservation.failedChecks > 0) {
					this.setMessage("networkFailure", "Connection unstable. Verifying reservation...");
					return;
				}
			}
			this.reservation.isExpired = true;
			this.clearReservationTimers();
			try {
				if (this.reservation.paymentIntentId) {
					await apiService.cancelPaymentIntent(this.reservation.paymentIntentId);
				}
			} catch (err) {
				console.error(" ");
			}
			this.showStripePayment = false;
			this.toast.error("Reservation expired. Please book again.");
		},
		clearReservationTimers() {
			if (this.reservation.timer) {
				clearInterval(this.reservation.timer);
				this.reservation.timer = null;
			}
			if (this.reservation.poller) {
				clearInterval(this.reservation.poller);
				this.reservation.poller = null;
			}
		},
		handleOffline() {
			this.reservation.isOffline = true;
			this.setMessage("networkFailure", "You're offline. Trying to reconnect...");
			this.toast.warning("You're offline. Trying to reconnect...");
		},
		handleOnline() {
			this.reservation.isOffline = false;
			this.networkFailureMessage = "";
			this.setMessage("networkOnline", "Back online. Resuming...");
			this.toast.success("Back online. Resuming...");
		},
		setMessage(type, text, duration = 5000) {
			clearTimeout(this.messageTimeout);
			this.successMessage = "";
			this.errorMessage = "";
			this.infoMessage = "";
			this.warningMessage = "";
			this.networkFailureMessage = "";
			this.networkOnlineMessage = "";
			if (type === "success") {
				this.successMessage = text;
			} else if (type === "info") {
				this.infoMessage = text;
			} else if (type === "warning") {
				this.warningMessage = text;
			} else if (type === "networkFailure") {
				this.networkFailureMessage = text;
				return;
			} else if (type === "networkOnline") {
				this.networkOnlineMessage = text;
			} else {
				this.errorMessage = text;
			}
			this.messageTimeout = setTimeout(() => {
				this.successMessage = "";
				this.errorMessage = "";
				this.infoMessage = "";
				this.warningMessage = "";
				this.networkFailureMessage = "";
				this.networkOnlineMessage = "";
			}, duration);
		},
		beforeUnmount() {
			if (this.otpInterval) {
				clearInterval(this.otpInterval);
			}
		}
	},
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");
</style>